import { Check, Copy, Link2, Link2Off } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { dashboardApi } from "../api";
import { useT } from "../i18n";
import type { ShareLink } from "../types";
import { formatDate } from "../utils";

/**
 * Share this piece of work with somebody outside the account (backlog item 10).
 *
 * Only appears on DELIVERED work. Sharing a request that has not been made yet
 * would send a stakeholder to an empty page, which is worse than no button.
 *
 * The link is shown in full and copyable, because the whole point is to paste it
 * into an email. What it costs is stated beside it — when it stops working, and
 * how many people have opened it — so a client can see whether the person they
 * sent it to ever looked.
 */
export default function ShareWork({
  requestId,
  delivered,
}: {
  requestId: string;
  /** No files delivered yet means there is nothing to show anybody. */
  delivered: boolean;
}) {
  const t = useT();
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLinks((await dashboardApi.shareLinks(requestId)).links);
    } catch {
      // A share panel that cannot load is not worth a red toast over the work
      // itself; it renders nothing and the rest of the page is unaffected.
      setLinks([]);
    }
  }, [requestId]);

  useEffect(() => { if (delivered) void load(); }, [delivered, load]);

  if (!delivered || !links) return null;

  const active = links.filter((l) => l.active);

  async function create() {
    setBusy(true);
    try {
      await dashboardApi.createShareLink(requestId);
      toast.success(t("share.created"));
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.createLink"));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(token: string) {
    setBusy(true);
    try {
      await dashboardApi.revokeShareLink(token);
      toast.success(t("share.revoked"));
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.revokeLink"));
    } finally {
      setBusy(false);
    }
  }

  async function copy(token: string) {
    const url = `${window.location.origin}/s/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(token);
      // Confirmed in place rather than by a toast: the button IS the feedback,
      // and a toast for a copy is noise on a page somebody is working in.
      setTimeout(() => setCopied((c) => (c === token ? null : c)), 1800);
    } catch {
      // Clipboard is blocked outside a secure context and in some embedded
      // browsers. Say so rather than silently doing nothing.
      toast.error(t("share.copyFailed"));
    }
  }

  return (
    <section className="share-work">
      <div className="share-work-head">
        <Link2 size={16} />
        <div>
          <strong>{t("share.title")}</strong>
          <span>{t("share.intro")}</span>
        </div>
        <button type="button" className="button button-secondary button-table" disabled={busy} onClick={() => void create()}>
          {t("share.create")}
        </button>
      </div>

      {active.length > 0 && (
        <ul className="share-link-list">
          {active.map((link) => (
            <li key={link.token}>
              <code>{`${window.location.origin}/s/${link.token}`}</code>
              <div className="share-link-meta">
                <span>{t("share.expires", { date: formatDate(link.expires_at) })}</span>
                <span>{link.view_count > 0
                  ? t("share.opened", { n: link.view_count })
                  : t("share.notOpened")}</span>
              </div>
              <div className="share-link-actions">
                <button type="button" className="rate-edit" onClick={() => void copy(link.token)}>
                  {copied === link.token ? <><Check size={13} /> {t("share.copied")}</> : <><Copy size={13} /> {t("share.copy")}</>}
                </button>
                <button type="button" className="rate-edit danger" disabled={busy} onClick={() => void revoke(link.token)}>
                  <Link2Off size={13} /> {t("share.revoke")}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
